import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "./User.css";

import UserTable from "./UserTable";
import UserModal from "./UserModal";
import PaginationControl from "./PaginationControl";
import SearchInput from "./SearchInput";
import { Skeleton } from "primereact/skeleton";

const UserList = () => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    mo_id: "",
    tls_id: "",
  });

  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const modalRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 2000);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    fetchUsers(1, debouncedSearch, true);
  }, [debouncedSearch]);

  const fetchUsers = async (page = 1, search = "", refreshAll = false) => {
    setTableLoading(true);
    try {
      const res = await API.get("/users", {
        params: {
          page,
          per_page: perPage,
          search,
        },
      });

      // data untuk tabel
      setUsers(res.data.data.data);
      setTotalPages(res.data.meta.last_page);
      setTotalData(res.data.meta.total);

      // Buat dapetin seluruh data untuk UserModal
      if (refreshAll) {
        const fullRes = await API.get("/users", {
          params: { per_page: 9999 },
        });
        setAllUsers(fullRes.data.data.data);
      }

      setTableLoading(false);
    } catch (err) {
      setTableLoading(false);
      toast.error("Sesi habis. Silakan login ulang.");
      navigate("/login");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formEl = e.currentTarget;

    if (!formEl.checkValidity()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    try {
      // data dasar
      const dataToSend = {
        name: form.name,
        email: form.email,
        role: form.role,
      };

      // password hanya dikirim jika ada
      if (form.password) {
        dataToSend.password = form.password;
      }

      // ====== KIRIM mo_id & tls_id SESUAI ROLE ======
      if (form.role === "tls") {
        dataToSend.mo_id = form.mo_id; // wajib
        dataToSend.tls_id = null;
      }

      if (form.role === "sales") {
        dataToSend.mo_id = form.mo_id; // wajib
        dataToSend.tls_id = form.tls_id; // wajib
      }

      if (
        [
          "admin",
          "user",
          "operational",
          "mo",
          "super-user",
          "cs-online",
        ].includes(form.role)
      ) {
        dataToSend.mo_id = null;
        dataToSend.tls_id = null;
      }

      setSubmitLoading(true);

      if (editId) {
        await API.put(`/users/${editId}`, dataToSend);
        toast.info("User berhasil diperbarui.");
      } else {
        await API.post("/users", dataToSend);
        toast.success("User berhasil ditambah.");
      }

      // Reset form
      setForm({
        name: "",
        email: "",
        password: "",
        role: "",
        mo_id: "",
        tls_id: "",
      });

      setEditId(null);
      fetchUsers(1, debouncedSearch, true);

      const modalInstance =
        Modal.getInstance(modalRef.current) || new Modal(modalRef.current);
      modalInstance.hide();
      setValidated(false);

      document.body.classList.remove("modal-open");
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
      setSubmitLoading(false);
    } catch (err) {
      console.log(err);

      const errorMessage =
        err.response?.data?.message || err.message || "Gagal menyimpan user!";
      toast.error(errorMessage);
      setSubmitLoading(false);
    }
  };

  const handleEdit = (user) => {
    setForm({
      name: user.name,
      email: user.email,
      role: user.role,
      password: "",
      mo_id: user.mo_id || "",
      tls_id: user.tls_id || "",
    });

    setEditId(user.id);
    const modal = new Modal(modalRef.current);
    modal.show();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Yakin?",
      text: "User akan dihapus secara permanen.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
    });
    if (result.isConfirmed) {
      try {
        await API.delete(`/users/${id}`);
        toast.success("User berhasil dihapus!");
        fetchUsers(1, debouncedSearch, true);
      } catch (err) {
        toast.error("Gagal menghapus user.");
      }
    }
  };

  return (
    <div className="container mt-4">
      {tableLoading ? (
        <div className="d-flex justify-content-between gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <Skeleton width="80px" height="20px" />
            <Skeleton width="90px" height="20px" />
            <Skeleton width="300px" height="20px" />
          </div>

          <div className="d-flex align-items-center gap-2">
            <Skeleton width="130px" height="20px" />
            <Skeleton width="110px" height="20px" />
            <Skeleton width="110px" height="20px" />
            <Skeleton width="110px" height="20px" />
          </div>
        </div>
      ) : (
        <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <label className="me-2">Tampilkan:</label>
            <select
              className="form-select d-inline-block w-auto"
              value={perPage}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPerPage(newSize);
                setPage(1);
                fetchUsers(1, search, newSize);
              }}
            >
              {[10, 25, 50, 100, 500].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="ms-2">
              Page {page} of {totalPages} from{" "}
              {totalData.toLocaleString("id-ID")} User
            </span>
          </div>
          <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
            <button
              className="btn btn-success"
              onClick={() => {
                setEditId(null);
                setForm({ name: "", email: "", role: "", password: "" });
                const modal = new Modal(modalRef.current);
                modal.show();
              }}
            >
              <i className="fa-solid fa-plus"></i> Tambah
            </button>
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      )}

      <UserTable
        users={users}
        tableLoading={tableLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        page={page}
        perPageSize={perPage}
        allUsers={allUsers}
        search={search}
        setSearch={setSearch}
      />
      <PaginationControl
        page={page}
        totalPages={totalPages}
        totalData={totalData}
        perPage={perPage}
        onPrev={() => {
          const newPage = page - 1;
          setPage(newPage);
          fetchUsers(newPage, search, perPage);
        }}
        onNext={() => {
          const newPage = page + 1;
          setPage(newPage);
          fetchUsers(newPage, search, perPage);
        }}
        onGoToPage={(newPage) => {
          setPage(newPage);
          fetchUsers(newPage, search, perPage);
        }}
        onChangeRows={(newSize) => {
          setPerPage(newSize);
          setPage(1);
          fetchUsers(1, search, newSize);
        }}
      />

      <UserModal
        modalRef={modalRef}
        form={form}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        editId={editId}
        setEditId={setEditId}
        setForm={setForm}
        validated={validated}
        submitLoading={submitLoading}
        setValidated={setValidated}
        allUsers={allUsers}
      />
    </div>
  );
};

export default UserList;
